import React, { AnchorHTMLAttributes, ButtonHTMLAttributes } from "react";
import classNames from "classnames";
import Link, { LinkProps } from "next/link";

import { Themes } from "custom-types/Themes";

import Spinner from "components/Icons/Spinner";
import IconSvg from "components/IconSvg";

const variants = {
  primary: "primary",
  secondary: "secondary",
  tertiary: "tertiary",
  text: "text",
} as const;

type ButtonVariant = keyof typeof variants;

const getElementType = (
  href?: string,
  hasOnClick?: boolean,
  isTypeSubmit?: boolean
): React.ElementType =>
  href ? Link : isTypeSubmit || hasOnClick ? "button" : "div";

export type ButtonProps = (
  | ButtonHTMLAttributes<HTMLButtonElement>
  | (AnchorHTMLAttributes<HTMLAnchorElement> & LinkProps)
  | AnchorHTMLAttributes<HTMLDivElement>
) & {
  iconFilePath?: string;
  size?: "full" | "small";
  theme?: Themes.light;
  variant?: ButtonVariant;
  width?: "full";
  isLoading?: boolean;
  loadingText?: React.ReactNode;
  iconDimensions?: string;
};

/**
 * Buttons are used to indicate actions, interactions, or links.
 *
 * **Primary** buttons are used to indicate a primary call to action. There should be one per page at most.
 *
 * **Tertiary** buttons can be used when there are two 'primary' calls to action in a single section, or occasionally to open navigation menus.
 *
 * **Text** buttons don't look like typical buttons — they're just bold, underlined text.
 *
 * **Small** buttons are usually used for groups of buttons.
 */
const Button = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement | HTMLDivElement,
  ButtonProps
>(
  (
    {
      children,
      className,
      iconDimensions,
      iconFilePath,
      isLoading = false,
      loadingText,
      onClick,
      size,
      theme,
      variant,
      width,
      ...others
    },
    ref
  ) => {
    const classes = classNames(
      "button",
      { "button--primary": variant === variants.primary },
      // secondary intentionally omitted; it is the default style of button
      { "button--tertiary": variant === variants.tertiary },
      { "button--text": variant === variants.text },
      { "button--small": size === "small" },
      { "button--full": width === "full" || size === "full" },
      { "button--light": theme === Themes.light },
      { "flex justify-center items-center": !!iconFilePath },
      className
    );

    const iconMargin = size === "small" ? "mr-xs" : "mr-sm";
    const iconSize =
      size === "small"
        ? { height: "16", width: "16" }
        : iconDimensions === "large"
        ? { height: "24", width: "24" }
        : { height: "20", width: "20" };

    const loadingOrDisabled =
      isLoading || (others as ButtonHTMLAttributes<HTMLButtonElement>).disabled;

    const Element = getElementType(
      (others as AnchorHTMLAttributes<HTMLAnchorElement>).href,
      !!onClick,
      (others as ButtonHTMLAttributes<HTMLButtonElement>).type === "submit"
    );

    return (
      <Element
        onClick={loadingOrDisabled ? undefined : onClick}
        ref={ref}
        className={classes}
        disabled={loadingOrDisabled}
        {...others}
      >
        {!!iconFilePath && (
          <IconSvg
            className={iconMargin}
            {...iconSize}
            filePath={iconFilePath}
          />
        )}
        {isLoading ? (
          <div className="flex flex-row items-center">
            <span className="">{loadingText}</span>
            <div className="pl-2">
              <Spinner padding={false} width={10} height={10} color="yellow" />
            </div>
          </div>
        ) : (
          children
        )}
      </Element>
    );
  }
);

export default Button;
